import { useDebounceFn } from '@vueuse/core';
import { getPointResolution } from 'ol/proj';
import type View from 'ol/View';
import type { Ref } from 'vue';
import { ref } from 'vue';

import { isNonZero } from '@nora-monorepo/shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { formatScaleLineDistance } from '#nora-ui/utils/distance';

export interface UseMapScaleLineOptions {
  map: NoraMap;
  minWidth?: number;
  maxWidth?: number;
}

export interface UseMapScaleLineReturn {
  width: Ref<number>;
  distanceText: Ref<string>;
  setup: () => Promise<void>;
  cleanup: () => void;
}

export function useMapScaleLine(options: UseMapScaleLineOptions): UseMapScaleLineReturn {
  const { map, minWidth = 70, maxWidth = 180 } = options;

  const width = ref<number>(0);
  const distanceText = ref<string>('');

  let resizeObserver: ResizeObserver | null = null;

  function getMetersPerPixel(view: View): number | null {
    const resolution = view.getResolution();
    const projection = view.getProjection();
    const center = view.getCenter();

    if (!isNonZero(resolution) || !center) {
      return null;
    }

    const mPerPx = getPointResolution(projection, resolution, center, 'm');
    return Number.isFinite(mPerPx) ? mPerPx : null;
  }

  const updateScale = useDebounceFn((): void => {
    const olMap = map.getOlMap();

    if (!olMap) {
      return;
    }

    const view = olMap.getView();
    const metersPerPixel = getMetersPerPixel(view);

    if (!isNonZero(metersPerPixel)) {
      return;
    }

    const maxMeters = maxWidth * metersPerPixel;
    const { valueMeters, label } = formatScaleLineDistance(maxMeters);

    distanceText.value = label;
    width.value = Math.max(
      minWidth,
      Math.min(maxWidth, Math.round(valueMeters / metersPerPixel)),
    );
  }, 10, { maxWait: 30 });

  const setup = async (): Promise<void> => {
    const olMap = map.getOlMap();

    if (!olMap) {
      return;
    }

    const view = olMap.getView();

    olMap.on('moveend', updateScale);
    view.on('change:resolution', updateScale);
    view.on('change:center', updateScale);

    if ('ResizeObserver' in window) {
      const targetEl = olMap.getTargetElement();
      resizeObserver = new ResizeObserver(() => {
        void updateScale();
      });
      resizeObserver.observe(targetEl);
    }

    await updateScale();
  };

  const cleanup = (): void => {
    const olMap = map.getOlMap();

    if (!olMap) {
      return;
    }

    const view = olMap.getView();

    olMap.un('moveend', updateScale);
    view.un('change:resolution', updateScale);
    view.un('change:center', updateScale);

    resizeObserver?.disconnect();
    resizeObserver = null;
  };

  return {
    width,
    distanceText,
    setup,
    cleanup,
  };
}
